import {
  Dropdown,
  DropdownMenuItemType,
  IDropdownOption,
  IDropdownProps,
  ISearchBoxProps,
  SearchBox,
} from 'office-ui-fabric-react';
import React from 'react';

interface SearchableDropdown {
  dropdownProps: IDropdownProps;
  searchBoxProps?: ISearchBoxProps;
}

const SearchableDropdown: React.FC<SearchableDropdown> = (props) => {
  const [searchText, setSearchText] = React.useState<string>('');
  const liveRegionRef = React.useRef<HTMLDivElement>(null);
  const timerRef = React.useRef<number>();

  const { dropdownProps, searchBoxProps } = props;
  const { options } = dropdownProps;

  const filteredOptions = options.filter(
    (option) =>
      !option.disabled &&
      option.text.toLowerCase().includes(searchText.toLowerCase()),
  );

  React.useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    if (!searchText) {
      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = '';
      }
      return;
    }
    timerRef.current = window.setTimeout(() => {
      if (liveRegionRef.current) {
        const count = filteredOptions.length;
        // Clear and set in separate frames so screen reader detects the change
        liveRegionRef.current.textContent = '';
        requestAnimationFrame(() => {
          if (liveRegionRef.current) {
            liveRegionRef.current.textContent =
              `${count} result${count !== 1 ? 's' : ''} found`;
          }
        });
      }
    }, 500);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [searchText]);

  function renderOption(
    option?: IDropdownOption,
    defaultRender?: (option?: IDropdownOption) => JSX.Element | null,
  ): JSX.Element | null {
    if (
      option &&
      option.itemType === DropdownMenuItemType.Header &&
      option.key === 'FilterHeader'
    ) {
      return (
        <SearchBox
          {...searchBoxProps}
          // Bolow is hack to focus search box if its not focussed, happens when a item is selected
          componentRef={(ref) =>
            new Promise((resolve) => setTimeout(resolve, 10)).then(() => {
              if (ref?.hasFocus) ref?.focus();
            })
          }
          onChange={(ev, newValue) => setSearchText(newValue ?? '')}
          underlined
        />
      );
    }
    if (dropdownProps.onRenderOption) {
      return dropdownProps.onRenderOption(option, defaultRender);
    }
    if (defaultRender) {
      return defaultRender(option);
    }
    return null;
  }

  return (
    <>
      <Dropdown
        {...dropdownProps}
        ariaLabel="country dropdown"
        options={[
          {
            key: 'FilterHeader',
            text: '-',
            itemType: DropdownMenuItemType.Header,
          },
          {
            key: 'divider_filterHeader',
            text: '-',
            itemType: DropdownMenuItemType.Divider,
          },
          ...options.map((option) =>
            filteredOptions.includes(option)
              ? option
              : { ...option, hidden: true },
          ),
        ]}
        onRenderOption={renderOption}
        onDismiss={() => setSearchText('')}
      />
      <div
        ref={liveRegionRef}
        aria-live="assertive"
        aria-atomic="true"
        role="status"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
    </>
  );
};

export default SearchableDropdown;
